import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import JsdhNotifikaceService from '../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import { QueryError } from '../api/QueryError';
import { Odpoved, Udalost, DisplejStylOdpovedi } from '../api/JsdhNotifikaceService/generated';

import OdpovediHasicu from './OdpovediHasicu';
import StatistikyOdpovedi from './StatistikyOdpovedi';

interface OdpovediWrapperProps {
    aktivniPoplach: Udalost;
    deviceToken?: string;
    showSpecializace?: boolean;
    showDatumCasVyhlaseni?: boolean;
    displejStyl?: DisplejStylOdpovedi;
    adaptivniLayout?: boolean;
}

const OdpovediWrapper: React.FC<OdpovediWrapperProps> = ({ aktivniPoplach, deviceToken, showSpecializace = true, showDatumCasVyhlaseni = false, displejStyl = 'STRIPE', adaptivniLayout = false }) => {
    const poplachId = useMemo(() => aktivniPoplach?.id, [aktivniPoplach?.id]);

    const {
        data: odpovedi = [],
        isLoading,
        isFetched,
        error,
    } = useQuery<Odpoved[], QueryError>({
        queryKey: ['odpovedi', poplachId, deviceToken],
        queryFn: () =>
            poplachId
                ? JsdhNotifikaceService.getOdpovediByPoplachId(poplachId, deviceToken || undefined)
                : Promise.resolve([]),
        enabled: Boolean(poplachId),
        refetchInterval: 4000,
        refetchOnWindowFocus: false,
        staleTime: 3000,
    });

    const { jedou, nejedou, pocetVD, pocetS, pocetH } = useMemo(() => {
        let jedou = 0, nejedou = 0, pocetVD = 0, pocetS = 0, pocetH = 0;

        for (const odpoved of odpovedi) {
            if (odpoved.odpoved === 'JEDU') jedou++;
            if (odpoved.odpoved === 'NEJEDU') nejedou++;

            // Prioritní počítání funkcí - každý uživatel se počítá jen jednou
            // Priorita: VD (Velitel družstva) > S (Strojník) > H (Hasič)
            if (odpoved.odpoved === 'JEDU' && odpoved.funkce?.length) {
                const kody = odpoved.funkce.map(f => f.kod);
                if (kody.includes('VD')) {
                    pocetVD++;
                } else if (kody.includes('S')) {
                    pocetS++;
                } else if (kody.includes('H')) {
                    pocetH++;
                }
            }
        }

        return { jedou, nejedou, pocetVD, pocetS, pocetH };
    }, [odpovedi]);

    return (
        <Grid container spacing={0} sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }}>
            {/* Statistiky */}
            <Grid size={{ xs: 12 }} sx={{ flexShrink: 0 }}>
                <StatistikyOdpovedi
                    pocetJedu={jedou}
                    pocetNejedu={nejedou}
                    pocetVD={pocetVD}
                    pocetS={pocetS}
                    pocetH={pocetH}
                    showDatumCasVyhlaseni={showDatumCasVyhlaseni}
                    isLoading={!isFetched}
                    aktivniPoplach={aktivniPoplach}
                />
            </Grid>

            {/* Karty hasičů */}
            <Grid size={{ xs: 12 }} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <OdpovediHasicu
                    odpovedi={odpovedi} test={false}
                    showSpecializace={showSpecializace}
                    displejStyl={displejStyl}
                    adaptivniLayout={adaptivniLayout}
                    casVyhlaseni={aktivniPoplach.casVyhlaseni}
                />
            </Grid>
        </Grid>
    );
};

export default React.memo(OdpovediWrapper);
